import { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PaymentMethodsModal from "./PaymentMethodsModal";

export default function EditSaleUI({
  sale,
  productTypes,
  allProducts,
  selectedProducts,
  selectedType,
  setSelectedType,
  paymentMethods,
  setPaymentMethods,
  discount,
  setDiscount,
  handleProductChange,
  handleSubmit,
}) {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [openSidebar, setOpenSidebar] = useState(true);
  const containerRef = useRef(null);
  const [buttonTop, setButtonTop] = useState("50%");

  useEffect(() => {
    if (containerRef.current) {
      const height = containerRef.current.offsetHeight;
      setButtonTop(`${height / 2 - 20}px`);
    }
  }, [sale]);

  if (!sale) {
    return (
      <Box
        sx={{
          ml: 1,
          mt: 2,
          width: "30%",
          padding: 2,
          border: "1px solid #ccc",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "90%",
        }}
      >
        Seleccione una venta para editar
      </Box>
    );
  }

  const subtotal = Object.values(selectedProducts).reduce((sum, { product_id, quantity }) => {
    const prod = allProducts.find(p => p.id === product_id);
    if (!prod) return sum;
    const isDec = prod.sell_method === "Decimal";
    const unitPrice = isDec
      ? prod.price * quantity / prod.quantity_reference
      : prod.price * quantity;
    return sum + unitPrice;
  }, 0);

  return (
    <Box
      sx={{
        ml: 1,
        mt: 2,
        width: "30%",
        padding: 2,
        border: "1px solid #ccc",
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        maxHeight: "70vh",
        position: "relative",
      }}
    >
      <Box>
        <Typography variant="h6">Editar venta</Typography>


        {/* Tipos de producto y productos */}
        <Box sx={{ display: "flex", mt: 2, position: "relative" }}>
          {/* Sidebar de tipos */}
          {openSidebar && (
            <Box
              sx={{
                width: "30%",
                borderRight: "1px solid #ccc",
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                zIndex: 2,
              }}
            >
              <List>
                {productTypes.map((typeObj) => (
                  <ListItemButton
                    key={typeObj.type}
                    selected={selectedType === typeObj.type}
                    onClick={() => {
                      setSelectedType(typeObj.type);
                      setOpenSidebar(false);
                    }}
                  >
                    <ListItemText primary={typeObj.type} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          )}

          {/* Boton para mostrar/ocultar sidebar */}
          {!openSidebar && (
            <IconButton 
              onClick={() => setOpenSidebar(true)}
              size="small"
              sx={{
                position: "absolute",
                left: 0,
                top: buttonTop,
                transform: "translateY(-50%)",
                zIndex: 10,
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                width: 30,
                height: 50,
                padding: 0,
              }}
            >
              <ChevronRightIcon fontSize="small"/>
            </IconButton>
          )}

          {/* Productos */}
          <Box
            sx={{ 
              ml: openSidebar ? "30%" : "6%",
              width: "100%", 
              padding: 1.5, 
              overflowY: "auto", 
              height: "32vh",
              textAlign: "right", 
            }}
          >
            {allProducts
              .filter((product) => product.product_type?.type === selectedType)
              .map((product) => {
                const selected = selectedProducts[product.id];
                const isDecimal = product.sell_method === "Decimal";
                const value = selected?.quantity || 0;

                const displayValue = isDecimal
                  ? product.quantity_reference >= 1000 
                    ? `${value / 1000} kg` 
                    : `${value} gr`
                  : `${value}`;

                return (
                  <Box
                    key={product.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        maxWidth: 100, 
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {product.name}
                    </Typography>

                    {openSidebar ? (
                      <Typography sx={{ width: "36%", textAlign: "right" }}>{displayValue}</Typography>
                    ) : (
                      <TextField
                        type="number"
                        size="small"
                        sx={{ 
                          width: "36%", 
                          ...(isDecimal && {
                            '& input::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                            '& input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                            '& input[type=number]': { MozAppearance: 'textfield' }
                          })
                        }}
                        value={selected?.quantity || ""}
                        onChange={(e) =>
                          handleProductChange(product.id, e.target.value)
                        }
                        InputProps={{
                          endAdornment: isDecimal && (
                            <Typography sx={{ ml: 1 }}>{product.quantity_reference >= 1000 ? "kg" : "gr"}</Typography>
                          ),
                        }}
                        inputProps={{ min: 0 }}
                      />
                    )}
                  </Box>
                );
              })}
          </Box>
        </Box>

        {/* Método de pago */}
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setShowPaymentModal(true)}
            sx={{ fontSize: 13 }}
          >
            {paymentMethods.length > 0
              ? `Editar Métodos de Pago (${paymentMethods.length} añadido${paymentMethods.length > 1 ? "s" : ""})`
              : "Seleccionar Métodos de Pago"}
          </Button>
            
          <PaymentMethodsModal
            open={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            initialMethods={paymentMethods}
            initialExtra={discount}
            onSave={(methods, extra) => {
              setPaymentMethods(methods);
              setDiscount(extra);
            }}
            subtotal={subtotal}
          />
        </Box>

        {/* Botón de guardar */}
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          sx={{ mt: 2, width: "100%" }}
        >
          Guardar cambios
        </Button>
      </Box>
    </Box>
  );
}
